import { useEffect, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@components/ui/carousel";
import { type CarouselApi } from "@components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { SanityDocument } from "next-sanity";
import Image from "next/image";
import { urlFor } from "@/sanity/lib/image";
import { CarouselActionButton } from "./services-carousel-buttons";
import clsx from "clsx";
import { useRouter } from "next/navigation";
import { animatePageOut } from "@/lib/pageAnimate";
import { Card } from "@/components/ui/card";

interface ServicesCarouselProps {
  services: SanityDocument[];
  pathname?: string;
}

function ServicesCarousel({
  services,
  pathname,
}: Readonly<ServicesCarouselProps>) {
  const [carouselAPI, setCarouselAPI] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const router = useRouter();

  useEffect(() => {
    if (!carouselAPI) {
      return;
    }
    setCurrent(carouselAPI?.selectedScrollSnap());

    carouselAPI.on("select", () => {
      setCurrent(carouselAPI.selectedScrollSnap());
    });
  }, [carouselAPI]);

  const handleClick = (href: string) => {
    const url = `${pathname}/${href}`;
    animatePageOut(url, router);
  };

  return (
    <>
      <Carousel
        plugins={[
          Autoplay({
            delay: 5000,
          }),
        ]}
        opts={{
          align: "center",
          loop: true,
        }}
        setApi={setCarouselAPI}
        className="pt-10"
      >
        <CarouselContent className="-ml-10 h-full max-h-112">
          {services?.map(({ title, description, image, slug }, index) => (
            <CarouselItem
              key={title}
              className={clsx(
                current === index
                  ? "pointer-events-auto"
                  : "pointer-events-none",
                "group relative ml-10 basis-full cursor-pointer overflow-hidden pl-0 text-center shadow-md lg:basis-1/2",
              )}
              onClick={() => handleClick(slug.current)}
            >
              <Card
                className={clsx(
                  current === index ? "scale-100" : "scale-75",
                  "flex h-full w-full rounded-none border-none transition-all duration-500",
                )}
              >
                <Image
                  src={urlFor(image).url()}
                  alt={image.alt}
                  width={4000}
                  height={4000}
                  className="object-cover blur-sm brightness-50 grayscale-[50%] filter-none group-hover:filter"
                  loading="lazy"
                />
                <div className="background-opacity-15 absolute left-0 top-0 flex h-full w-full flex-col items-start justify-end gap-4 bg-gradient-to-t from-slate-900 via-transparent to-transparent px-12 py-5 text-white transition-all duration-500 md:px-10 xl:px-20 xl:py-10">
                  <h2 className="text-left text-2xl font-bold transition-all duration-500 hover:text-dark-highlighter group-hover:translate-y-0 lg:translate-y-20 lg:text-2xl xl:text-4xl">
                    {title}
                  </h2>
                  <p className="mt-2 hidden max-w-lg text-left opacity-0 transition-all duration-500 group-hover:opacity-100 lg:block">
                    {description}
                  </p>
                </div>
              </Card>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      <div className="mt-5 flex w-full justify-center gap-5">
        <CarouselActionButton
          direction="left"
          onClick={() => carouselAPI?.scrollPrev()}
        />
        <CarouselActionButton
          direction="right"
          onClick={() => carouselAPI?.scrollNext()}
        />
      </div>
    </>
  );
}

export default ServicesCarousel;
